import React, { useCallback, useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { useHistory } from 'react-router';
import { useLazyQuery } from '@apollo/react-hooks';
import { logEvent } from 'expo-firebase-analytics';

import { Table } from '../../core-ui';
import { Header, HeaderNavigationBar, ErrorMessage } from '../../components';
import { spacing } from '../../constants/theme';
import { GET_SUBMIT_FORM_COMPETITION } from '../../graphql/queries';
import {
  SubmitFormCompetitionAdvanceSearch,
  SubmitFormCompetitionAdvanceSearchVariables,
} from '../../generated/SubmitFormCompetitionAdvanceSearch';
import { routePaths } from '../../constants/routes';
import { pageTitle } from '../../constants/pageTitle';

export default function SubmittedCompetitionForm() {
  const history = useHistory<{
    formId: number;
    name: string;
  }>();
  const { formId, name } = history.location.state;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [
    getSubmitFormCompetition,
    { data, loading, error, refetch },
  ] = useLazyQuery<
    SubmitFormCompetitionAdvanceSearch,
    SubmitFormCompetitionAdvanceSearchVariables
  >(GET_SUBMIT_FORM_COMPETITION, {
    variables: { skip: page * rowsPerPage, first: rowsPerPage, formId: formId },
  });

  const onChangePage = useCallback(
    (newPage: number) => {
      refetch?.({
        skip: page * rowsPerPage,
        first: rowsPerPage,
        formId: formId,
      });
      setPage(newPage);
    },
    [page, rowsPerPage, formId, refetch],
  );

  useEffect(() => {
    const eventLog = async () => {
      await logEvent('page_view', {
        page_title: pageTitle.SubmittedCompetitionForm,
      });
    };
    eventLog();
  }, []);

  useEffect(() => {
    getSubmitFormCompetition();
  }, [getSubmitFormCompetition]);

  const onRowsChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.currentTarget.value));
    setPage(0);
  };

  const onBackPress = useCallback(() => history.push(routePaths.onlineForms), [
    history,
  ]);

  return (
    <View style={styles.root}>
      <HeaderNavigationBar
        backDestinationText={t(['Formulir Online', 'Online Forms'])}
        onBackButtonPress={onBackPress}
      />
      <Header withSeparator title={name} />
      <Table
        error={!!error && !!data}
        data={(data && data.SubmitFormCompetitionAdvanceSearch.row) ?? []}
        loading={loading}
        dataCount={(data && data.SubmitFormCompetitionAdvanceSearch.total) || 0}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onRowsChange}
        rowsPerPage={rowsPerPage}
        structure={{
          name: {
            headerTitle: t(['nama', 'name']),
          },
          storeName: {
            headerTitle: t(['toko', 'toko']),
          },
          acctNumber: {
            headerTitle: t(['nomor akun', 'account number']),
          },
        }}
      />
      {!!error && (
        <ErrorMessage
          error={error}
          action={t(['mengambil data formulir', 'retrieve the formulir data'])}
          onPress={refetch}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { padding: spacing.xlarge },
});
